import { forkJoin, Observable, of } from 'rxjs';
import { GroupedGrid, GroupedGridColumn, PrimaryTierMap, PcodePrimaryTierMap, PoliticalTierLookupItem } from '@models/lookup';
import {
    RateCardMetricAlertCalculations,
    RateCardPricingTier,
    RateCardMetricValueV2,
    RateCardMetricRowV2,
    DynamicRateCardMetricValueV2,
} from '@models/rate-card/rate-card';
import { numberFormatter, removeCommasFromValue } from '@shared/helpers/functions/helpers';
import { cloneDeep } from 'lodash';
import { switchMap } from 'rxjs/operators';

// links and landing pages saved before political tabs were enumerable still carry political_rate_card
const POLITICAL_RATE_CARD_TAB_IDS = ['political_rate_card', 'political_rate_card_tab_1'];

export const isSameRateCardTab = (tabId: string, requestedTabId: string): boolean =>
    tabId === requestedTabId || (POLITICAL_RATE_CARD_TAB_IDS.includes(tabId) && POLITICAL_RATE_CARD_TAB_IDS.includes(requestedTabId));

const compareValues = (val1: number, comparator: string, val2: number): boolean => {
    if (comparator === '<=') {
        return val1 <= val2;
    } else if (comparator === '<') {
        return val1 < val2;
    } else if (comparator === '=') {
        return val1 === val2;
    } else if (comparator === '>') {
        return val1 > val2;
    } else if (comparator === '>=') {
        return val1 >= val2;
    } else if (comparator === '!=') {
        return val1 !== val2;
    }
    return false;
};

export const calculateRateCardAlerts2 = <
    T = RateCardPricingTier | PoliticalTierLookupItem,
    U extends object = object,
    V extends RateCardMetricValueV2 = RateCardMetricValueV2,
>(
    values: RateCardMetricRowV2<V>[],
    alerts: RateCardMetricAlertCalculations[],
    groupedColumns: GroupedGridColumn[],
    primaryTierMap: PrimaryTierMap,
    pcodePrimaryTierMap: PcodePrimaryTierMap,
    tiers: T[],
    copyFunc: (
        value: RateCardMetricRowV2<V>,
        primaryTierMap: PrimaryTierMap,
        pcodePrimaryTierMap: PcodePrimaryTierMap,
        cell: V,
        dateTier: string,
        tiers: T[],
        newValues: { [key: string]: DynamicRateCardMetricValueV2 },
    ) => void,
    passThroughParams: U,
): Observable<GroupedGrid<RateCardMetricRowV2<V>> & { tiers: T[] } & U> => {
    if (values.length === 0) {
        return of({ data: [], groupedColumns, tiers, ...passThroughParams });
    }
    const rcIdStart = {};
    const rcIdEnd = {};
    values.forEach((value, index) => {
        // @ts-expect-error TS7053: implicit any
        if (rcIdStart[value.rateCardId] === undefined) {
            // @ts-expect-error TS7053: implicit any
            rcIdStart[value.rateCardId] = index;
        }
        // @ts-expect-error TS7053: implicit any
        rcIdEnd[value.rateCardId] = index;
    });
    // @ts-expect-error TS7053: implicit any
    const obsArr = [];
    Object.keys(rcIdStart).forEach((rcID) => {
        // @ts-expect-error TS7053: implicit any
        obsArr.push(calculateAlertsForIdV2(values.slice(rcIdStart[rcID], rcIdEnd[rcID] + 1), alerts, rcID, primaryTierMap));
    });
    // @ts-expect-error TS7053: implicit any
    return forkJoin(obsArr).pipe(
        switchMap((rcAlerts) => {
            const allAlerts = {};
            // @ts-expect-error TS7053: implicit any
            rcAlerts.forEach((rcAlert) => (allAlerts[rcAlert[0]] = rcAlert[1]));
            values.forEach((value) => {
                value.alerts = {
                    ...(value.alerts || {}),
                    // @ts-expect-error TS7053: implicit any
                    ...(allAlerts[value.rateCardId].alerts || {}),
                };
                const newValues = {};
                Object.keys(value.value).forEach((dateTier) => {
                    ' 2025-01-01-T1';

                    const cell = value.value[dateTier];

                    cell.alerts = {
                        ...(cell.alerts || {}),
                        // @ts-expect-error TS7053: implicit any
                        ...(allAlerts[value.rateCardId][dateTier]?.alerts || {}),
                    };
                    copyFunc(value, primaryTierMap, pcodePrimaryTierMap, cell, dateTier, tiers, newValues);
                    // @ts-expect-error TS7053: implicit any
                    newValues[dateTier] = cell;
                });
                value.value = newValues;
            });
            return of({ data: values, groupedColumns, tiers, ...passThroughParams });
        }),
    );
};

export const dynamicRateCardCopyCell = (
    value: RateCardMetricRowV2<DynamicRateCardMetricValueV2>,
    primaryTierMap: PrimaryTierMap,
    pcodePrimaryTierMap: PcodePrimaryTierMap,
    cell: DynamicRateCardMetricValueV2,
    dateTier: string,
    tiers: RateCardPricingTier[],
    newValues: { [key: string]: DynamicRateCardMetricValueV2 },
): void => {
    if (value.isTiered && (primaryTierMap || pcodePrimaryTierMap)) {
        if (cell.isPcode) {
            cell.isPrimary = pcodePrimaryTierMap.tierName === cell.pricingTier;
        } else if (
            value.daypartId != null &&
            value.spotTypeId != null &&
            primaryTierMap &&
            primaryTierMap[value.daypartId] &&
            primaryTierMap[value.daypartId][value.spotTypeId]
        ) {
            cell.isPrimary = primaryTierMap[value.daypartId][value.spotTypeId] === cell.pricingTier;
        }
    } else if (!value.isTiered) {
        if (cell.isTotal) {
            tiers.forEach((tier) => {
                const newCell = cloneDeep(cell);
                const col = dateTier.split('-')[0];
                newCell.pricingTier = tier.name;
                newValues[`${col}-${tier.name}`] = newCell;
            });
        } else {
            const date: string = dateTier.split('-').slice(0, 3).join('-');
            tiers.forEach((tier) => {
                const newCell = cloneDeep(cell);
                newCell.pricingTier = tier.name;
                newValues[`${date}-${tier.name}`] = newCell;
            });
        }
    }
};

export const sportRateCardCopyCell = (
    value: RateCardMetricRowV2<DynamicRateCardMetricValueV2>,
    primaryTierMap: PrimaryTierMap,
    _pcodePrimaryTierMap: PcodePrimaryTierMap,
    cell: DynamicRateCardMetricValueV2,
    _tierKey: string,
    _tiers: RateCardPricingTier[],
    _newValues: { [key: string]: DynamicRateCardMetricValueV2 },
): void => {
    if (
        value.isTiered &&
        primaryTierMap &&
        value.daypartId != null &&
        value.spotTypeId != null &&
        primaryTierMap[value.daypartId]?.[value.spotTypeId]
    ) {
        cell.isPrimary = primaryTierMap[value.daypartId][value.spotTypeId] === cell.pricingTier;
    }
};

export const politicalRateCardCopyCell = (
    value: RateCardMetricRowV2,
    primaryTierMap: PrimaryTierMap,
    pcodePrimaryTierMap: PcodePrimaryTierMap,
    cell: RateCardMetricValueV2,
    dateTier: string,
    tiers: PoliticalTierLookupItem[],
    newValues: { [key: string]: RateCardMetricValueV2 },
) => {
    if (!value.isTiered) {
        const date: string = dateTier.split('-').slice(0, 3).join('-');
        tiers.forEach((tier) => {
            newValues[`${date}-${tier.name}`] = cloneDeep(cell);
        });
    }
};

export const sportPoliticalRateCardCopyCell = (
    value: RateCardMetricRowV2,
    primaryTierMap: PrimaryTierMap,
    pcodePrimaryTierMap: PcodePrimaryTierMap,
    cell: RateCardMetricValueV2,
    dateTier: string,
    tiers: PoliticalTierLookupItem[],
    newValues: { [key: string]: RateCardMetricValueV2 },
) => {
    if (!value.isTiered) {
        // this card carries the period on the row, so its keys are the tier alone --
        // recovering a date from the key the way the political card does would produce
        // keys like "T1-T2" that match no column
        tiers.forEach((tier) => {
            newValues[tier.name] = cloneDeep(cell);
        });
    }
};

export const digitalRateCardCopyCell = (
    value: RateCardMetricRowV2<DynamicRateCardMetricValueV2>,
    primaryTierMap: PrimaryTierMap,
    pcodePrimaryTierMap: PcodePrimaryTierMap,
    cell: DynamicRateCardMetricValueV2,
    dateTier: string,
    tiers: RateCardPricingTier[],
    newValues: Record<string, DynamicRateCardMetricValueV2>,
): void => {
    if (value.isTiered) {
        tiers.forEach((tier) => {
            if (tier.name === cell.pricingTier) {
                cell.isPrimary = tier.is_primary;
            }
        });
    } else if (!value.isTiered) {
        const date: string = dateTier.split('-').slice(0, 3).join('-');
        tiers.forEach((tier) => {
            const newCell = cloneDeep(cell);
            newCell.pricingTier = tier.name;
            newValues[`${date}-${tier.name}`] = newCell;
        });
    }
};

const calculateAlertsForIdV2 = (
    values: RateCardMetricRowV2[],
    alerts: RateCardMetricAlertCalculations[],
    rcID: string,
    primaryTierMap?: PrimaryTierMap,
): Observable<[string, Record<string, unknown>]> => {
    const rcAlerts = { alerts: {} };
    values.forEach((value) => {
        alerts
            .filter((alert) => alert.primaryMetric === value.metricTypeId)
            .forEach((alert) => {
                Object.keys(value.value).forEach((dateTier) => {
                    const cell = value.value[dateTier];
                    // @ts-expect-error TS7053: implicit any
                    let primaryDateTier;
                    let primaryTierId;

                    // @ts-expect-error TS7053: implicit any
                    if (rcAlerts[dateTier] === undefined) {
                        // @ts-expect-error TS7053: implicit any
                        rcAlerts[dateTier] = { alerts: {} };
                    }
                    if (
                        primaryTierMap &&
                        value.daypartId != null &&
                        value.spotTypeId != null &&
                        primaryTierMap[value.daypartId] &&
                        primaryTierMap[value.daypartId][value.spotTypeId]
                    ) {
                        primaryTierId = primaryTierMap[value.daypartId][value.spotTypeId];
                        primaryDateTier = dateTier.split('-').slice(0, 3).join('-') + '-' + primaryTierId;
                        // @ts-expect-error TS7053: implicit any
                        if (rcAlerts[primaryDateTier] === undefined) {
                            // @ts-expect-error TS7053: implicit any
                            rcAlerts[primaryDateTier] = { alerts: {} };
                        }
                    }

                    // @ts-expect-error TS7053: implicit any
                    const alertCount = (alertName, isAlert) => {
                        if (value.isTiered) {
                            // @ts-expect-error TS7053: implicit any
                            rcAlerts.alerts[alertName] = (rcAlerts.alerts[alertName] || 0) + (isAlert ? 1 : 0);
                            // @ts-expect-error TS7053: implicit any
                            rcAlerts[dateTier].alerts[alertName] =
                                // @ts-expect-error TS7053: implicit any
                                (rcAlerts[dateTier].alerts[alertName] || 0) + (isAlert ? 1 : 0);
                        } else if (value.isTiered === false) {
                            // @ts-expect-error TS7053: implicit any
                            rcAlerts.alerts[alertName] = (rcAlerts.alerts[alertName] || 0) + (isAlert ? 1 : 0);
                            // @ts-expect-error TS7053: implicit any
                            if (primaryDateTier) {
                                // @ts-expect-error TS7053: implicit any
                                rcAlerts[primaryDateTier].alerts[alertName] =
                                    // @ts-expect-error TS7053: implicit any
                                    (rcAlerts[primaryDateTier].alerts[alertName] || 0) + (isAlert ? 1 : 0);
                            }
                            cell.alerts = {
                                ...(cell.alerts || {}),
                                ...{ [alertName]: isAlert ? 1 : 0 },
                            };
                        } else {
                            cell.alerts = {
                                ...(cell.alerts || {}),
                                ...{ [alertName]: isAlert ? 1 : 0 },
                            };
                        }
                    };

                    if (cell.value) {
                        const thresholds = alert.thresholds.length ? alert.thresholds : [''];
                        const tiers = alert.tiers.length ? alert.tiers : [];
                        thresholds.forEach((threshold) => {
                            const isAlert = doesCellTriggerAlertV2(
                                alert,
                                cell,
                                value,
                                values,
                                rcID,
                                dateTier,
                                // @ts-expect-error TS7053: implicit any
                                threshold,
                                tiers,
                            );
                            const alertName = alert.alertId + threshold;
                            alertCount(alertName, isAlert);
                        });
                    }
                });
            });
    });
    return of([rcID, rcAlerts]);
};

const doesCellTriggerAlertV2 = (
    alert: RateCardMetricAlertCalculations,
    cell: RateCardMetricValueV2,
    row: RateCardMetricRowV2,
    values: RateCardMetricRowV2[],
    rcID: string,
    dateTier: string,
    threshold: number,
    tiers: string[],
): boolean => {
    if (cell.isTotal || cell.isPcode) {
        return false; // never trigger alerts on the totals columns
    }

    const tierId = dateTier.split('-').slice(-1).join('-');
    if (tiers.length > 0 && !tiers.includes(tierId)) {
        return false;
    }

    // use the number formatter to round the value based on the format
    // This is just for mathematical comparison purposes, so we use number format rather than currency/percent
    const mainRowFormatter = row.format?.format
        ? (value: number) => Number(removeCommasFromValue(numberFormatter(value, row.format.format)))
        : (value: number) => value;
    const primaryValue = alert.useOriginalPrimary ? cell.value?.original : cell.value?.current;
    let isAlert = false;
    if (alert.isApproved) {
        isAlert = cell.overrideData ? cell.overrideData.isApproved === false : false;
    } else if (alert.alertNull) {
        isAlert = primaryValue === null;
    } else if (alert.compareOverride) {
        const formattedOriginal = mainRowFormatter(cell.value.original);
        isAlert = compareValues(
            mainRowFormatter(cell.value.current),
            alert.comparisonOperator,
            formattedOriginal + ((formattedOriginal * threshold) / 100) * (alert.negativeThreshold ? -1 : 1),
        );
    } else if (alert.compareRecValue) {
        if (cell.overrideData?.recValue) {
            const formattedRecValue = mainRowFormatter(cell.overrideData.recValue);
            isAlert = compareValues(
                mainRowFormatter(primaryValue),
                alert.comparisonOperator,
                formattedRecValue + ((formattedRecValue * threshold) / 100) * (alert.negativeThreshold ? -1 : 1),
            );
        }
    } else if (!alert.secondaryMetric) {
        isAlert = primaryValue != null ? compareValues(mainRowFormatter(primaryValue), alert.comparisonOperator, threshold) : false;
    } else {
        const secondaryMetricRow = values.find((value) => value.rateCardId === rcID && value.metricTypeId === alert.secondaryMetric);
        let secondaryValue: number | null = null;
        if (secondaryMetricRow) {
            const metric = secondaryMetricRow.value[dateTier]?.value;
            secondaryValue = alert.useOriginalSecondary ? metric?.original ?? null : metric?.current ?? null;
        }
        if (secondaryValue != null && secondaryMetricRow) {
            const secondaryRowFormatter = secondaryMetricRow.format?.format
                ? (value: number) => Number(removeCommasFromValue(numberFormatter(value, secondaryMetricRow.format.format)))
                : (value: number) => value;
            if (primaryValue && secondaryMetricRow?.value?.[dateTier]?.value && secondaryMetricRow.value[dateTier]?.value.current != null) {
                const formattedSecondaryValue = secondaryRowFormatter(secondaryValue);
                isAlert = compareValues(
                    mainRowFormatter(primaryValue),
                    alert.comparisonOperator,
                    formattedSecondaryValue + ((formattedSecondaryValue * threshold) / 100) * (alert.negativeThreshold ? -1 : 1),
                );
            }
        }
    }
    return isAlert;
};
